import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"

import styled from "styled-components/native"

import { MediaImageFile } from "@treefort/api-spec"
import { colorWithAlpha } from "@treefort/lib/color"
import { getOptimizedImageSource } from "@treefort/lib/get-optimized-image-source"
import icons from "@treefort/tokens/app/icons"

import { Blurhash } from "../blurhash"
import Icon, { IconSize } from "../icon"
import ImageContained from "../image-contained"
import { useTokens } from "../tokens-provider"
import Touchable from "../touchable"

const MAX_IMAGE_HEIGHT = 1080

const BLURHASH_RESOLUTION_X = 2
const BLURHASH_RESOLUTION_Y = 4

const { containerStyle } = StyleSheet.create({
  containerStyle: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
})

const PosterImage = styled(ImageContained)`
  background-color: ${(props) => colorWithAlpha(props.theme.colors.white, 0.2)};
`

const PlayIconContainer = styled.View<{
  imagePositionTop: number
  imagePositionBottom: number
}>`
  position: relative;
  width: ${(props) => props.theme.videoPlayer.playIconBackdropSize}px;
  height: ${(props) => props.theme.videoPlayer.playIconBackdropSize}px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Ensure the play icon is vertically centered with the poster image */
  margin-top: ${(props) =>
    (props.imagePositionTop - props.imagePositionBottom) / 2}px;
`

const PlayIconBackdrop = styled.View`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: ${(props) => props.theme.borderRadius.roundedFull}px;
  background-color: ${(props) => props.theme.colors.black};
  opacity: ${(props) => props.theme.videoPlayer.playIconBackdropOpacity};
`

const BlurhashContainer = styled.View`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.5;
`

export default function Poster({
  poster,
  onPress,
}: {
  poster?: MediaImageFile
  onPress?: () => void
}): JSX.Element | null {
  const { t } = useTranslation()
  const { tokens } = useTokens()
  const imageContainerStyles = useMemo(
    () =>
      ({
        top: tokens.spacing.medium,
        right: tokens.spacing.xlarge,
        bottom: tokens.spacing.xlarge,
        left: tokens.spacing.xlarge,
        position: "absolute",
      }) as const,
    [tokens],
  )
  const { width, height } = poster?.original || {}
  const image = poster ? (
    <PosterImage
      uri={getOptimizedImageSource(poster.original.url, {
        height: MAX_IMAGE_HEIGHT,
      })}
      borderRadius="roundedLarge"
      containerStyle={imageContainerStyles}
      imageSize={width && height ? { width, height } : undefined}
    />
  ) : null
  const blurhash = poster?.original.blurhash ? (
    <BlurhashContainer>
      <Blurhash
        hash={poster.original.blurhash}
        width="100%"
        height="100%"
        resolutionX={BLURHASH_RESOLUTION_X}
        resolutionY={BLURHASH_RESOLUTION_Y}
      />
    </BlurhashContainer>
  ) : null

  return image && onPress ? (
    <>
      {blurhash}
      <Touchable
        id="poster-image"
        containerStyle={containerStyle}
        style={containerStyle}
        onPress={onPress}
        feedback="none"
        aria-label={t("Watch now")}
        role="button"
      >
        {image}
        <PlayIconContainer
          imagePositionTop={imageContainerStyles.top}
          imagePositionBottom={imageContainerStyles.bottom}
        >
          <PlayIconBackdrop />
          <Icon
            size={tokens.videoPlayer.playIconSize as IconSize}
            source={icons.play}
            color="highContrast"
          />
        </PlayIconContainer>
      </Touchable>
    </>
  ) : image ? (
    <>
      {blurhash}
      <View style={containerStyle}>{image}</View>
    </>
  ) : null
}
